import type { InstallationConfigPayload } from '@/api/installation.api';

/**
 * GUARDADO POR PESTAÑA de «Mi negocio».
 *
 * `PUT /api/installation-config` reemplaza la configuración ENTERA: no existe
 * un guardado parcial del lado del servidor. Mientras hubo un solo botón
 * "Guardar cambios" eso no molestaba a nadie. Con un botón por pestaña sí: si
 * la pestaña Catálogo mandara su propio borrador completo, se llevaría puesto
 * lo que alguien acabara de guardar en Glosario desde otra pantalla.
 *
 * La regla de acá: se manda SIEMPRE el payload completo, pero armado con la
 * config que está hoy en el servidor + únicamente las claves que esa pestaña
 * edita. Guardar una pestaña no puede cambiar nada de otra.
 */

/** Las pestañas de la pantalla, en el orden en que se ven. */
export const PESTANAS_DE_INSTALACION = [
  { id: 'negocio', nombre: 'Negocio' },
  { id: 'tiendas', nombre: 'Tiendas' },
  { id: 'agente', nombre: 'Agente' },
  { id: 'catalogo', nombre: 'Catálogo' },
  { id: 'crm', nombre: 'CRM' },
  { id: 'mensajes', nombre: 'Mensajes' },
  { id: 'trabajos', nombre: 'Trabajos' },
  { id: 'inspector', nombre: 'Inspector' },
  { id: 'glosario', nombre: 'Glosario' },
  { id: 'copiloto', nombre: 'Copiloto' },
  { id: 'pendientes', nombre: 'Pendientes' },
] as const satisfies readonly { id: string; nombre: string }[];

export type PestanaDeInstalacion = (typeof PESTANAS_DE_INSTALACION)[number]['id'];

/** La pestaña con la que abre la pantalla si la dirección no dice otra cosa. */
export const PESTANA_POR_DEFECTO: PestanaDeInstalacion = 'negocio';

/**
 * Qué claves del PUT escribe cada pestaña. Es el contrato del guardado: lo que
 * no está acá, la pestaña no lo toca.
 *
 * `trabajos` aparece en dos pestañas porque el dato está partido en la base y
 * la pantalla ya no: los MODELOS (con sus meses de garantía) se editan en
 * Catálogo, y el resto de las reglas en Trabajos. La mezcla fina la hace
 * `fusionarPestana`.
 */
export const CLAVES_DE_PESTANA: Record<
  PestanaDeInstalacion,
  readonly (keyof InstallationConfigPayload)[]
> = {
  negocio: ['businessName', 'currencyPrefix', 'slaHours', 'notionDatabaseId'],
  // A6 · Las tres tiendas, el horario y las reglas de reparto de Osito.
  tiendas: ['osito'],
  // El prompt del agente y las reglas de cómo/qué envía se editan juntos.
  agente: ['agent', 'envio', 'conversacion'],
  catalogo: ['catalog', 'trabajos'],
  crm: ['crm'],
  mensajes: ['messages'],
  trabajos: ['trabajos'],
  inspector: ['inspector'],
  glosario: ['glosario'],
  copiloto: ['copiloto'],
  pendientes: ['pendientes'],
};

/** El nombre visible de una pestaña (para los textos de "guardar"/"descartar"). */
export function nombreDePestana(pestana: PestanaDeInstalacion): string {
  return PESTANAS_DE_INSTALACION.find((p) => p.id === pestana)?.nombre ?? pestana;
}

/** `?tab=…` de la dirección → una pestaña real. Lo que no existe cae en Negocio. */
export function pestanaDeLaUrl(raw: string | null | undefined): PestanaDeInstalacion {
  const encontrada = PESTANAS_DE_INSTALACION.find((p) => p.id === raw);
  return encontrada ? encontrada.id : PESTANA_POR_DEFECTO;
}

/**
 * El pedazo de `trabajos` que edita cada pestaña.
 *
 * Catálogo escribe SOLO `modelos`; Trabajos escribe todo MENOS `modelos`. Así
 * las dos pestañas comparten la misma clave de la base sin pisarse.
 */
function trabajosFusionados(
  actual: InstallationConfigPayload,
  pestana: PestanaDeInstalacion,
  borrador: InstallationConfigPayload,
): InstallationConfigPayload['trabajos'] {
  if (pestana === 'catalogo') return { ...actual.trabajos, modelos: borrador.trabajos.modelos };
  return { ...borrador.trabajos, modelos: actual.trabajos.modelos };
}

/**
 * El payload que se manda al guardar UNA pestaña: la config del servidor con
 * las claves de esa pestaña reemplazadas por las del borrador.
 */
export function fusionarPestana(
  actual: InstallationConfigPayload,
  pestana: PestanaDeInstalacion,
  borrador: InstallationConfigPayload,
): InstallationConfigPayload {
  const fusionado: InstallationConfigPayload = { ...actual };
  for (const clave of CLAVES_DE_PESTANA[pestana]) {
    if (clave === 'trabajos') {
      fusionado.trabajos = trabajosFusionados(actual, pestana, borrador);
      continue;
    }
    // El resto de las claves son bloques enteros: se reemplazan tal cual.
    Object.assign(fusionado, { [clave]: borrador[clave] });
  }
  return fusionado;
}

/**
 * ¿Esta pestaña tiene cambios sin guardar?
 *
 * Se compara el resultado de fusionarla contra la config actual: si mandar esa
 * pestaña no cambiaría nada, no hay nada que guardar. Comparar así (y no campo
 * por campo) es lo que hace imposible que la barra diga "sin cambios" mientras
 * el botón sí mandaría algo distinto.
 */
export function pestanaTieneCambios(
  actual: InstallationConfigPayload,
  borrador: InstallationConfigPayload,
  pestana: PestanaDeInstalacion,
): boolean {
  return JSON.stringify(fusionarPestana(actual, pestana, borrador)) !== JSON.stringify(actual);
}

/** Todas las pestañas con cambios sin guardar, en el orden de la pantalla. */
export function pestanasConCambios(
  actual: InstallationConfigPayload,
  borrador: InstallationConfigPayload,
): PestanaDeInstalacion[] {
  return PESTANAS_DE_INSTALACION.map((p) => p.id).filter((id) =>
    pestanaTieneCambios(actual, borrador, id),
  );
}
